import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { useSaunaBasePrice } from "../hooks/useSaunaBasePrice";

const models = [
{ name: "VERBIER", saunaKey: "verbier", fallback: 249000, desc: "En kompakt badstue med karakteristisk form og et tydelig arkitektonisk uttrykk, og store glassflatten.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/4df33f500_SAUNAVEIBER_2.jpg", href: "/sauna-verbier", size: "4–6 personer", dims: "340 × 310 cm", altText: "LYKKE VERBIER – praktisk badstue med skråtak" },
{ name: "HYTTE", saunaKey: "hytte", fallback: 269000, desc: "Inspirert av klassiske nordiske hytter. Modellen er utviklet for å gli naturlig inn i tradisjonelle hytteområder.", image: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/7048e1aed_HYTTE_BADSTUE_GRNNTAK.jpg", href: "/sauna-hytte", size: "4–6 personer", dims: "370 × 310 cm", altText: "LYKKE HYTTE – klassisk badstue med skråtak" },
{ name: "ASPEN", saunaKey: "aspen", fallback: 299000, desc: "En større modell med integrert utvendig dusj og liten terrasse som gir mulighet for avkjøling.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/a2d3913c5_Aspen1.jpg", href: "/sauna-aspen", size: "6–8 personer", dims: "420 × 310 cm", altText: "LYKKE ASPEN – robust badstue modell" }];


function ModelCard({ model }) {
  const { basePrice, loading } = useSaunaBasePrice(model.saunaKey, model.fallback);
  return (
    <a href={model.href} className="group cursor-pointer block bg-secondary/30 border border-border/40 p-8 hover:border-border transition-all duration-300 hover:bg-secondary/50">
      <h3 className="font-heading text-3xl md:text-4xl font-light mb-3 transition-colors duration-300 group-hover:text-foreground/70">LYKKE {model.name}</h3>
      <p className="text-foreground/60 leading-relaxed mb-6 max-w-sm transition-colors duration-300 group-hover:text-foreground/40" style={{ fontSize: "23px" }}>{model.desc}</p>
      <div className="aspect-[4/3] overflow-hidden mb-6 bg-secondary/50 relative">
        <img
          src={model.image}
          alt={model.altText}
          className="w-full h-full object-cover group-hover:scale-105 transition-all duration-[1.5s] group-hover:brightness-50"
          width="800"
          height="600"
          loading="lazy"
          decoding="async" />
        
      </div>
      <div className="space-y-1.5 text-xl font-body border-t border-border/40 pt-4 transition-colors duration-300">
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Størrelse: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{model.size}</strong></p>
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Dimensjoner: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{model.dims}</strong></p>
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Fra: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{loading ? "..." : `${basePrice.toLocaleString("nb-NO")} NOK`}</strong></p>
      </div>
    </a>);

}

export default function SaunaCollectionFjel() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
      <>
      <Navbar />
      
      <section className="relative h-screen w-full overflow-hidden">
        <img
            src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/7004d600a_Verbier_1.jpg"
            alt="LYKKE FJELL Kolekcja – praktiske badstuer med skråtak"
            className="absolute inset-0 w-full h-full object-cover"
            width="1920"
            height="1080"
            loading="eager"
            fetchpriority="high"
            decoding="async" />
          
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-32 md:pb-40 px-6 md:px-12 max-w-[1440px] mx-auto">
          <p className="text-base tracking-widest-2xl font-body uppercase text-white/50 mb-6">
            Koleksjon
          </p>
          <h1 className="font-heading text-white text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-light leading-[0.95] mb-6">
            Vi skaper
            <br />
            <span className="italic">badstuer.</span>
          </h1>
          <p className="text-white/80 text-3xl md:text-4xl leading-relaxed max-w-2xl font-light">
            Varme, ro og velvære, i harmoni med naturen.
          </p>
          <p className="text-white/70 text-base md:text-base tracking-widest-2xl font-body uppercase">
            Skråtak · praktisk og elegant
          </p>
        </div>

        <button
            onClick={() => {
              document.querySelector("#models")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-white transition-colors animate-bounce">
            
          <ChevronDown size={28} strokeWidth={1} />
        </button>
      </section>

      <section className="py-16 md:py-24 bg-secondary/50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl md:text-5xl font-light leading-[1.2] mb-6">
              SKRÅTAK
            </h2>
            <p className="font-body leading-relaxed text-foreground/70" style={{ fontSize: "23px" }}>
              LYKKE FJELL er en kolleksjon badstuer med rene linjer og et rolig arkitektonisk uttrykk. Skråtaket gir modellene en tydelig form som passer naturlig inn i både moderne og tradisjonelle omgivelser.
              <br />
              <br />
              Den høyere fronten og den lave bakdelen skaper en balansert profil og gir rommene gode proporsjoner innvendig. Skråtaket leder vann og snø naturlig bort fra konstruksjonen og gir samtidig badstuen et lett og harmonisk uttrykk.
              <br />
              <br />
              Kolleksjonen er utviklet for å passe like godt ved sjøen, i skogen, på fjellet eller i hagen, med former og materialer inspirert av nordisk byggeskikk. Alle modellene er utviklet med fokus på riktig varmefordeling, naturlig luftgjennomstrømning og gode høyder som skaper et jevnt og behagelig löyly.
            </p>
          </div>
        </div>
      </section>

      <section id="models" className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            {models.map((model) =>
              <ModelCard key={model.name} model={model} />
              )}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 bg-secondary/50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">
                God stemning
              </p>
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light leading-[1.1] mb-8">
                Ekte
                <br />
                <span className="italic">materialer</span>
              </h2>
              <p className="font-body leading-relaxed text-foreground/70 mb-6" style={{ fontSize: "23px" }}>
                Alle LYKKE badstuer bygges i tre og naturlige materialer valgt for sin holdbarhet, varme og karakter.
                <br /><br />
                Når badstuen varmes opp, fylles rommet med en behagelig duft som skaper en rolig og avslappende stemning. Det er nettopp den følelsen vi elsker med en ekte badstue.
              </p>
            </div>
            <div className="aspect-[3/4] overflow-hidden rounded-lg bg-transparent flex items-center justify-center">
              <img src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/f4a2a3e59_vellykket-badstue_stacked-full_clay.png" alt="LYKKE FJELL – illustrasjon av alle badstuemodeller" className="w-full h-full object-contain" width="600" height="800" loading="lazy" decoding="async" />
            </div>
            

              
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">
            Kom i gang
          </p>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light mb-8">
            Finn badstuen
            <br />
            <span className="italic">som passer deg</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <button
                onClick={() => window.location.href = "/process"}
                className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300">
              BLI KJENT MED PROSESSEN
            </button>
            <button
                onClick={() => window.location.href = "/sauna-ly"}
                className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-foreground/30 text-foreground/70 hover:border-[#7a6f63] hover:bg-[#7a6f63] hover:text-white transition-all duration-300">
              LYKKE LY
            </button>
            <button
                onClick={() => window.location.href = "/sauna-panorama"}
                className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-foreground/30 text-foreground/70 hover:border-[#7a6f63] hover:bg-[#7a6f63] hover:text-white transition-all duration-300">
              LYKKE PANORAMA
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
    </motion.div>);

}